import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { ShieldCheck, Plus, Trash2, Pencil } from 'lucide-react';
import nurseryQuarantineService from '../../services/nurseryQuarantine';
import nurseryService from '../../services/nursery';
import sprayService from '../../services/spray';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { toNum } from '../../utils/numeric';
import { litresFor, windowText } from '../../utils/quarantine';
import {
  AU_STATES,
  QUARANTINE_CATEGORIES,
  QUARANTINE_CATEGORY_LABEL,
  QUARANTINE_UNITS,
  QUARANTINE_METHOD_LABEL,
  APPLIES_TO_LABEL,
} from '../../types/nurseryQuarantine';
import type {
  QuarantineAppliesTo,
  QuarantineCategory,
  QuarantineProtocol,
  QuarantineProtocolPayload,
  QuarantineUnit,
} from '../../types/nurseryQuarantine';
import type { NurseryLocation, NurseryTreeType } from '../../types/nursery';

/**
 * /nursery/quarantine — the organisation's quarantine protocols (ICA plan,
 * Phase 0).
 *
 * A protocol is one ICA number (ICA 29, PS 27…): the states whose orders go
 * out under it, how long a treatment stays valid before despatch, the mix at
 * its mixing rates, anything put on per plant (PS 27's iron chelate), the
 * litres of spray each tree type takes (or to run-off, for bare-root), and the
 * designated spray areas it may be carried out in.
 *
 * A nursery manager sets them up (Rob, 2026-10-13). The treatment of a load is
 * an ordinary spray, run with spray seats, so the spray module's safety checks
 * all apply; that comes in Phase 1. We ship no protocol content.
 */

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

interface ChemRow {
  category: QuarantineCategory; chemical_id: number | null; chemical_name: string; rate: string; unit: QuarantineUnit;
  /** Potted trees, bare-root to run-off, or both: each has its own mix (Rob, 2026-10-14). */
  appliesTo: QuarantineAppliesTo;
}
interface PotRow { chemical_id: number | null; chemical_name: string; amount: string; unit: QuarantineUnit; instructions: string }
interface VolRow { litres: string; to_run_off: boolean }

interface Draft {
  id: number | null;
  code: string;
  name: string;
  states: string[];
  window: string;
  windowUnit: 'hours' | 'days';
  /** One of the organisation's people; '' for none. */
  controllerId: string;
  /** A name saved before the controller was a person, shown until one is chosen. */
  legacyController: string | null;
  instructions: string;
  chemicals: ChemRow[];
  pots: PotRow[];
  /** By tree type id. A tree type with no entry is not covered. */
  volumes: Record<number, VolRow>;
  areas: number[];
}

const numOrNull = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function draftFrom(p: QuarantineProtocol | null): Draft {
  if (!p) {
    return {
      id: null, code: '', name: '', states: [], window: '', windowUnit: 'days', controllerId: '', legacyController: null, instructions: '',
      chemicals: [], pots: [], volumes: {}, areas: [],
    };
  }
  const days = p.window_hours % 24 === 0;
  return {
    id: p.id,
    code: p.code,
    name: p.name,
    states: [...p.destination_states],
    window: String(days ? p.window_hours / 24 : p.window_hours),
    windowUnit: days ? 'days' : 'hours',
    controllerId: p.controller_user_id == null ? '' : String(p.controller_user_id),
    legacyController: p.controller_user_id == null ? p.controller_name : null,
    instructions: p.instructions ?? '',
    chemicals: p.chemicals.map((c) => ({
      category: c.category, chemical_id: c.chemical_id, chemical_name: c.chemical_name ?? '',
      rate: String(toNum(c.rate_per_100l) ?? ''), unit: c.rate_unit, appliesTo: c.applies_to ?? 'both',
    })),
    pots: p.pot_treatments.map((t) => ({
      chemical_id: t.chemical_id, chemical_name: t.chemical_name ?? '',
      amount: String(toNum(t.amount_per_pot) ?? ''), unit: t.unit, instructions: t.instructions ?? '',
    })),
    volumes: Object.fromEntries(p.volumes.map((v) => [v.tree_type_id, {
      litres: v.litres_per_plant == null ? '' : String(toNum(v.litres_per_plant)), to_run_off: v.to_run_off,
    }])),
    areas: p.areas.map((a) => a.location_id),
  };
}

/** Type a name, pick a product. Searches the shared chemical register. */
function ChemicalPicker({ id, value, onPick }: { id: string; value: string; onPick: (chemId: number, name: string) => void }) {
  const [q, setQ] = useState(value);
  const [results, setResults] = useState<{ id: number; name: string }[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => { setQ(value); }, [value]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await sprayService.searchChemicals(q.trim(), 1, 8);
        if (res.success) setResults((res.data ?? []).map((c) => ({ id: c.id, name: c.trade_name || c.product_name })));
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <div className="relative">
      <input id={id} className={inputCls} value={q} placeholder="Search products"
        onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
      {open && results.length > 0 && (
        <ul className="absolute z-10 left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow max-h-56 overflow-y-auto">
          {results.map((r) => (
            <li key={r.id}>
              <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 break-words"
                onClick={() => { onPick(r.id, r.name); setQ(r.name); setOpen(false); }}>
                {r.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Editor({ draft, onChange, treeTypes, areas, people, onCancel, onSave, saving, error }: {
  draft: Draft;
  onChange: (d: Draft) => void;
  treeTypes: NurseryTreeType[];
  people: { id: number; name: string }[];
  areas: NurseryLocation[];
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}) {
  const set = (patch: Partial<Draft>) => onChange({ ...draft, ...patch });
  const setChem = (i: number, patch: Partial<ChemRow>) =>
    set({ chemicals: draft.chemicals.map((c, n) => (n === i ? { ...c, ...patch } : c)) });
  const setPot = (i: number, patch: Partial<PotRow>) =>
    set({ pots: draft.pots.map((c, n) => (n === i ? { ...c, ...patch } : c)) });
  const setVol = (ttId: number, row: VolRow | null) => {
    const next = { ...draft.volumes };
    if (row) next[ttId] = row; else delete next[ttId];
    set({ volumes: next });
  };

  return (
    <div className="bg-white border border-primary-200 rounded-xl p-4 space-y-5">
      <h3 className="text-base font-semibold text-neutral-800">{draft.id ? `Edit ${draft.code}` : 'New protocol'}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="qp-code" className="block text-sm font-medium text-neutral-700 mb-1">ICA number</label>
          <input id="qp-code" className={inputCls} maxLength={30} value={draft.code} placeholder="e.g. ICA-29"
            onChange={(e) => set({ code: e.target.value })} />
        </div>
        <div>
          <label htmlFor="qp-name" className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
          <input id="qp-name" className={inputCls} maxLength={150} value={draft.name} placeholder="e.g. ICA 29 Tasmania and Western Australia"
            onChange={(e) => set({ name: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <span className="block text-sm font-medium text-neutral-700 mb-1">Orders going to</span>
          <div className="flex flex-wrap gap-2">
            {AU_STATES.map((s) => {
              const on = draft.states.includes(s);
              return (
                <label key={s} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer ${on ? 'bg-primary-50 border-primary-300 text-primary-800' : 'border-neutral-200 text-neutral-700'}`}>
                  <input type="checkbox" checked={on}
                    onChange={() => set({ states: on ? draft.states.filter((x) => x !== s) : [...draft.states, s] })} />
                  {s}
                </label>
              );
            })}
          </div>
          <p className="text-xs text-neutral-500 mt-1">A state can go out under one protocol only. For cross dock orders it is the store's state.</p>
        </div>
        <div>
          <label htmlFor="qp-window" className="block text-sm font-medium text-neutral-700 mb-1">Treatment valid for</label>
          <div className="flex gap-2">
            <input id="qp-window" className={inputCls} type="text" inputMode="numeric" value={draft.window}
              onChange={(e) => set({ window: e.target.value })} />
            <select aria-label="Window unit" className="border border-neutral-300 rounded-lg px-2 py-2 text-sm"
              value={draft.windowUnit} onChange={(e) => set({ windowUnit: e.target.value as 'hours' | 'days' })}>
              <option value="days">days</option>
              <option value="hours">hours</option>
            </select>
          </div>
          <p className="text-xs text-neutral-500 mt-1">before despatch</p>
        </div>
        <div>
          <label htmlFor="qp-controller" className="block text-sm font-medium text-neutral-700 mb-1">Certification controller</label>
          <select id="qp-controller" className={inputCls} value={draft.controllerId}
            onChange={(e) => set({ controllerId: e.target.value })}>
            <option value="">Choose a person…</option>
            {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {draft.legacyController && !draft.controllerId && (
            <p className="text-xs text-amber-700 mt-1">{`Saved as "${draft.legacyController}". Choose the person it is.`}</p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="qp-instructions" className="block text-sm font-medium text-neutral-700 mb-1">Instructions printed on the record</label>
          <textarea id="qp-instructions" className={inputCls} rows={3} value={draft.instructions}
            onChange={(e) => set({ instructions: e.target.value })} />
        </div>
      </div>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-semibold text-neutral-700">The mix</h4>
          <Button variant="outline" size="sm" onClick={() => set({
            chemicals: [...draft.chemicals, { category: 'insecticide', chemical_id: null, chemical_name: '', rate: '', unit: 'mL', appliesTo: 'pots' }],
          })}><Plus className="h-4 w-4 mr-1" /> Add a chemical</Button>
        </div>
        <p className="text-xs text-neutral-500 mb-2">
          At the mixing rate, per 100 L of mix. Potted trees and bare-root sprayed to run-off each have their own mix: say which
          each chemical is for, or both. The operator picks one chemical in each category; several in a category are alternatives.
        </p>
        {draft.chemicals.length === 0 ? <p className="text-sm text-neutral-500">No chemicals yet.</p> : (
          <ul className="space-y-2">
            {draft.chemicals.map((c, i) => (
              <li key={i} className="border border-neutral-200 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)_auto_auto] gap-2 items-end">
                <div className="col-span-2 sm:col-span-1">
                  <label htmlFor={`qp-for-${i}`} className="block text-xs text-neutral-500 mb-1">Used for</label>
                  <select id={`qp-for-${i}`} className={inputCls} value={c.appliesTo}
                    onChange={(e) => setChem(i, { appliesTo: e.target.value as QuarantineAppliesTo })}>
                    {(['pots', 'run_off', 'both'] as const).map((k) => <option key={k} value={k}>{APPLIES_TO_LABEL[k]}</option>)}
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label htmlFor={`qp-cat-${i}`} className="block text-xs text-neutral-500 mb-1">Category</label>
                  <select id={`qp-cat-${i}`} className={inputCls} value={c.category}
                    onChange={(e) => setChem(i, { category: e.target.value as QuarantineCategory })}>
                    {QUARANTINE_CATEGORIES.map((k) => <option key={k} value={k}>{QUARANTINE_CATEGORY_LABEL[k]}</option>)}
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label htmlFor={`qp-chem-${i}`} className="block text-xs text-neutral-500 mb-1">Product</label>
                  <ChemicalPicker id={`qp-chem-${i}`} value={c.chemical_name}
                    onPick={(chemId, name) => setChem(i, { chemical_id: chemId, chemical_name: name })} />
                </div>
                <div>
                  <label htmlFor={`qp-rate-${i}`} className="block text-xs text-neutral-500 mb-1">Per 100 L</label>
                  <input id={`qp-rate-${i}`} className={inputCls} type="text" inputMode="decimal" value={c.rate}
                    onChange={(e) => setChem(i, { rate: e.target.value })} />
                </div>
                <div>
                  <label htmlFor={`qp-unit-${i}`} className="block text-xs text-neutral-500 mb-1">Unit</label>
                  <select id={`qp-unit-${i}`} className="border border-neutral-300 rounded-lg px-2 py-2 text-sm" value={c.unit}
                    onChange={(e) => setChem(i, { unit: e.target.value as QuarantineUnit })}>
                    {QUARANTINE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <button type="button" aria-label="Remove chemical"
                  onClick={() => set({ chemicals: draft.chemicals.filter((_, n) => n !== i) })}
                  className="p-2 rounded text-red-500 hover:bg-red-50 justify-self-end"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-semibold text-neutral-700">Per plant, before despatch</h4>
          <Button variant="outline" size="sm" onClick={() => set({
            pots: [...draft.pots, { chemical_id: null, chemical_name: '', amount: '', unit: 'g', instructions: '' }],
          })}><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>
        <p className="text-xs text-neutral-500 mb-2">A fixed amount on each plant, outside the spray, such as iron chelate for snails and slugs.</p>
        {draft.pots.length === 0 ? <p className="text-sm text-neutral-500">None.</p> : (
          <ul className="space-y-2">
            {draft.pots.map((t, i) => (
              <li key={i} className="border border-neutral-200 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto_auto] gap-2 items-end">
                <div className="col-span-2 sm:col-span-1">
                  <label htmlFor={`qp-pot-chem-${i}`} className="block text-xs text-neutral-500 mb-1">Product</label>
                  <ChemicalPicker id={`qp-pot-chem-${i}`} value={t.chemical_name}
                    onPick={(chemId, name) => setPot(i, { chemical_id: chemId, chemical_name: name })} />
                </div>
                <div>
                  <label htmlFor={`qp-pot-amt-${i}`} className="block text-xs text-neutral-500 mb-1">Per plant</label>
                  <input id={`qp-pot-amt-${i}`} className={inputCls} type="text" inputMode="decimal" value={t.amount}
                    onChange={(e) => setPot(i, { amount: e.target.value })} />
                </div>
                <div>
                  <label htmlFor={`qp-pot-unit-${i}`} className="block text-xs text-neutral-500 mb-1">Unit</label>
                  <select id={`qp-pot-unit-${i}`} className="border border-neutral-300 rounded-lg px-2 py-2 text-sm" value={t.unit}
                    onChange={(e) => setPot(i, { unit: e.target.value as QuarantineUnit })}>
                    {QUARANTINE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <button type="button" aria-label="Remove per-plant treatment"
                  onClick={() => set({ pots: draft.pots.filter((_, n) => n !== i) })}
                  className="p-2 rounded text-red-500 hover:bg-red-50 justify-self-end"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h4 className="text-sm font-semibold text-neutral-700 mb-1">Spray per plant</h4>
        <p className="text-xs text-neutral-500 mb-2">
          Litres of mix each tree type takes under this protocol, or sprayed to run-off (bare-root), when the tanks say how much went on.
          A tree type left blank is not covered: a load carrying it cannot be treated under this protocol until it is set.
        </p>
        <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
          {treeTypes.map((tt) => {
            const v = draft.volumes[tt.id];
            return (
              <li key={tt.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2">
                <span className="text-sm text-neutral-800 min-w-0 flex-1 break-words">{tt.name}</span>
                <label className="inline-flex items-center gap-1.5 text-sm text-neutral-700">
                  <input type="checkbox" checked={!!v?.to_run_off}
                    onChange={(e) => setVol(tt.id, e.target.checked ? { litres: '', to_run_off: true } : null)} />
                  To run-off
                </label>
                <div className="flex items-center gap-1.5">
                  <input aria-label={`Litres per plant, ${tt.name}`} className="w-20 border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
                    type="text" inputMode="decimal" disabled={!!v?.to_run_off} value={v?.litres ?? ''}
                    onChange={(e) => setVol(tt.id, e.target.value.trim() === '' ? null : { litres: e.target.value, to_run_off: false })} />
                  <span className="text-xs text-neutral-500">L each</span>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section>
        <h4 className="text-sm font-semibold text-neutral-700 mb-1">Designated spray areas</h4>
        {areas.length === 0 ? (
          <p className="text-sm text-amber-700">
            No quarantine spray areas yet. Add one on <Link className="underline" to="/nursery/locations">Locations</Link> as a
            {' '}"Quarantine spray area", then choose it here.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {areas.map((a) => {
              const on = draft.areas.includes(a.id);
              return (
                <label key={a.id} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer break-words ${on ? 'bg-primary-50 border-primary-300 text-primary-800' : 'border-neutral-200 text-neutral-700'}`}>
                  <input type="checkbox" checked={on}
                    onChange={() => set({ areas: on ? draft.areas.filter((x) => x !== a.id) : [...draft.areas, a.id] })} />
                  {a.name}
                </label>
              );
            })}
          </div>
        )}
      </section>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save protocol'}</Button>
      </div>
    </div>
  );
}

function ProtocolCard({ p, canEdit, onEdit, onToggle }: {
  p: QuarantineProtocol; canEdit: boolean; onEdit: () => void; onToggle: () => void;
}) {
  // One list per mix; a chemical for both is in each.
  const mixes = (['pots', 'run_off'] as const).map((m) => ({
    m,
    byCategory: QUARANTINE_CATEGORIES
      .map((k) => ({ k, items: p.chemicals.filter((c) => c.category === k && (!c.applies_to || c.applies_to === 'both' || c.applies_to === m)) }))
      .filter((g) => g.items.length > 0),
  }));
  const example = p.volumes.find((v) => !v.to_run_off && toNum(v.litres_per_plant) != null);
  return (
    <div className={`bg-white border rounded-xl p-4 space-y-3 ${p.active ? 'border-neutral-200' : 'border-dashed border-neutral-300 opacity-70'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-neutral-800 break-words">{p.code} · {p.name}</h3>
          <p className="text-xs text-neutral-500 break-words">
            {p.destination_states.length ? `Orders to ${p.destination_states.join(', ')}` : 'No states chosen'}
            {` · valid ${windowText(p.window_hours)} before despatch`}
            {p.controller_name && ` · controller ${p.controller_name}`}
            {!p.active && ' · not in use'}
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={onEdit}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
            <Button variant="outline" size="sm" onClick={onToggle}>{p.active ? 'Stop using' : 'Use again'}</Button>
          </div>
        )}
      </div>

      {p.chemicals.length === 0 ? (
        <p className="text-sm text-amber-700">No chemicals yet.</p>
      ) : mixes.map(({ m, byCategory }) => (
        <div key={m}>
          <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">{QUARANTINE_METHOD_LABEL[m]}</p>
          {byCategory.length === 0 ? (
            <p className="text-sm text-amber-700">No chemicals for this mix.</p>
          ) : (
            <dl className="space-y-1 text-sm">
              {byCategory.map((g) => (
                <div key={g.k} className="flex flex-wrap gap-x-2">
                  <dt className="text-neutral-500">{QUARANTINE_CATEGORY_LABEL[g.k]}:</dt>
                  <dd className="text-neutral-800 min-w-0 break-words">
                    {g.items.map((c) => `${c.chemical_name} ${toNum(c.rate_per_100l)} ${c.rate_unit}/100 L`).join(' or ')}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ))}
      {p.pot_treatments.map((t) => (
        <p key={t.id ?? t.chemical_id} className="text-sm text-neutral-700 break-words">
          Per plant: {t.chemical_name} {toNum(t.amount_per_pot)} {t.unit}
        </p>
      ))}

      <div>
        {p.volumes.length === 0 ? (
          <p className="text-sm text-amber-700">No tree types set: nothing can be treated under it yet.</p>
        ) : (
          <p className="text-sm text-neutral-700 break-words">
            {p.volumes.map((v) => `${v.tree_type_name} ${v.to_run_off ? 'to run-off' : `${toNum(v.litres_per_plant)} L`}`).join(' · ')}
          </p>
        )}
        {example && (
          <p className="text-xs text-neutral-500 mt-1">
            {`100 × ${example.tree_type_name} take ${litresFor(100, toNum(example.litres_per_plant) ?? 0)} L of mix.`}
          </p>
        )}
      </div>

      <p className={`text-sm break-words ${p.areas.length ? 'text-neutral-700' : 'text-amber-700'}`}>
        {p.areas.length
          ? `Sprayed at ${p.areas.map((a) => (a.farm_name ? `${a.name} (${a.farm_name})` : a.name)).join(', ')}`
          : 'No designated spray area chosen.'}
      </p>
    </div>
  );
}

function QuarantineProtocols() {
  const { hasAccess: canEdit } = useModuleLicense('nursery', ['manager', 'admin']);
  const [protocols, setProtocols] = useState<QuarantineProtocol[] | null>(null);
  const [treeTypes, setTreeTypes] = useState<NurseryTreeType[]>([]);
  const [areas, setAreas] = useState<NurseryLocation[]>([]);
  const [people, setPeople] = useState<{ id: number; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, tt, loc] = await Promise.all([
        nurseryQuarantineService.getProtocols(true),
        nurseryService.getTreeTypes(),
        nurseryService.getLocations({ location_type: 'quarantine_area', active: true }),
      ]);
      if (p.success) setProtocols(p.data);
      else setError(errorMessage(p, 'Could not load the quarantine protocols'));
      if (tt.success) setTreeTypes(tt.data.filter((t) => t.is_active));
      if (loc.success) setAreas(loc.data);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the quarantine protocols'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The staff to choose a controller from: only a manager edits, and only a manager may list them.
  useEffect(() => {
    if (!canEdit) return;
    nurseryQuarantineService.getPeople()
      .then((res) => { if (res.success) setPeople(res.data ?? []); })
      .catch(() => setPeople([]));
  }, [canEdit]);

  const save = async () => {
    if (!draft) return;
    const windowN = Number(draft.window);
    if (!draft.code.trim() || !draft.name.trim()) { setSaveError('Give it its ICA number and a name.'); return; }
    if (!Number.isInteger(windowN) || windowN < 1) { setSaveError('Say how long a treatment stays valid.'); return; }
    if (draft.chemicals.some((c) => c.chemical_id == null || !(numOrNull(c.rate) ?? 0))) {
      setSaveError('Every chemical in the mix needs a product and a rate per 100 L.'); return;
    }
    if (draft.pots.some((t) => t.chemical_id == null || !(numOrNull(t.amount) ?? 0))) {
      setSaveError('Every per-plant treatment needs a product and an amount.'); return;
    }
    const badVol = Object.entries(draft.volumes).find(([, v]) => !v.to_run_off && !((numOrNull(v.litres) ?? 0) > 0));
    if (badVol) { setSaveError('Litres per plant must be more than 0.'); return; }

    const payload: QuarantineProtocolPayload = {
      code: draft.code.trim(),
      name: draft.name.trim(),
      destination_states: draft.states,
      window_hours: draft.windowUnit === 'days' ? windowN * 24 : windowN,
      controller_user_id: draft.controllerId ? Number(draft.controllerId) : null,
      instructions: draft.instructions.trim() || null,
      chemicals: draft.chemicals.map((c) => ({
        category: c.category, chemical_id: c.chemical_id as number, rate_per_100l: Number(c.rate), rate_unit: c.unit,
        applies_to: c.appliesTo,
      })),
      pot_treatments: draft.pots.map((t) => ({
        chemical_id: t.chemical_id as number, amount_per_pot: Number(t.amount), unit: t.unit,
        instructions: t.instructions.trim() || null,
      })),
      volumes: Object.entries(draft.volumes).map(([id, v]) => ({
        tree_type_id: Number(id), to_run_off: v.to_run_off, litres_per_plant: v.to_run_off ? null : Number(v.litres),
      })),
      area_location_ids: draft.areas,
    };
    setSaving(true);
    setSaveError(null);
    try {
      const res = draft.id == null
        ? await nurseryQuarantineService.createProtocol(payload)
        : await nurseryQuarantineService.updateProtocol(draft.id, payload);
      if (!res.success) { setSaveError(errorMessage(res, 'Could not save the protocol')); return; }
      setDraft(null);
      load();
    } catch (e) {
      setSaveError(errorMessage(e, 'Could not save the protocol'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p: QuarantineProtocol) => {
    try {
      const res = await nurseryQuarantineService.updateProtocol(p.id, { active: !p.active });
      if (!res.success) { setError(errorMessage(res, 'Could not change the protocol')); return; }
      load();
    } catch (e) {
      setError(errorMessage(e, 'Could not change the protocol'));
    }
  };

  return (
    <>
      <PageHeader
        title="Quarantine protocols"
        subtitle="ICA treatments for loads going interstate"
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          Each protocol must match your own certification arrangement. FactreeTech supplies no protocol content:
          the chemicals, rates, volumes and windows are yours to enter and check.
        </div>
        {!canEdit && <p className="text-xs text-neutral-500">A Nursery Manager seat sets these up. You can see them.</p>}
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {canEdit && draft == null && (
          <Button variant="primary" size="sm" onClick={() => { setSaveError(null); setDraft(draftFrom(null)); }}>
            <Plus className="h-4 w-4 mr-1" /> New protocol
          </Button>
        )}
        {draft && (
          <Editor draft={draft} onChange={setDraft} treeTypes={treeTypes} areas={areas} people={people}
            onCancel={() => setDraft(null)} onSave={save} saving={saving} error={saveError} />
        )}

        {protocols == null ? (
          <div className="h-24 bg-neutral-100 rounded-xl animate-pulse" />
        ) : protocols.length === 0 ? (
          <div className="bg-white border border-dashed border-neutral-300 rounded-xl px-6 py-10 text-center">
            <p className="text-sm text-neutral-600">No quarantine protocols yet.</p>
            <p className="text-xs text-neutral-400 mt-1">Add one for each ICA your loads go out under.</p>
          </div>
        ) : (
          protocols.map((p) => (
            <ProtocolCard key={p.id} p={p} canEdit={canEdit}
              onEdit={() => { setSaveError(null); setDraft(draftFrom(p)); }}
              onToggle={() => toggle(p)} />
          ))
        )}
      </div>
    </>
  );
}

export default QuarantineProtocols;
